export function rgba2hex(color: string) {
	const values = color
		.replace(/rgba?\(/, '')
		.replace(/\)/, '')
		.replace(/[\s+]/g, '')
		.split(',') as any;

	const a = parseFloat('' + (values[3] || 1)),
		r = Math.floor(a * parseInt(values[0]) + (1 - a) * 255),
		g = Math.floor(a * parseInt(values[1]) + (1 - a) * 255),
		b = Math.floor(a * parseInt(values[2]) + (1 - a) * 255);

	return '#' +
		('0' + r.toString(16)).slice(-2) +
		('0' + g.toString(16)).slice(-2) +
		('0' + b.toString(16)).slice(-2);
}

export function hexToRgbA(hex: string, opacity = 1) {
	let c;

	if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
		c = hex.substring(1).split('');
		if (c.length === 3) {
			c = [c[0], c[0], c[1], c[1], c[2], c[2]];
		}
		c = '0x' + c.join('') as any;

		return 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + `,${opacity})`;
	}

	console.error('Bad Hex');

	return hex;
}

export const lighten = (hex: string, opacity = 1) => rgba2hex(hexToRgbA(hex, opacity));

/**
 * Vérifie si une couleur donnée est totalement transparente (donc "invisible").
 *
 * Gère plusieurs formats :
 * - HEX 8 caractères (#RRGGBBAA) → si AA = 00, c’est transparent.
 * - HEX 4 caractères (#RGBA) → si A = 0, c’est transparent.
 * - RGBA (rgba(r, g, b, a)) → si a = 0, c’est transparent.
 *
 * @param {string} color - La couleur à tester (ex: "#00000000", "#FFF0", "rgba(255,255,255,0)").
 * @returns {boolean} true si la couleur est totalement transparente, sinon false.
 */
export const checkIsInvisible = (color: string): boolean => {
	if (!color) return true;

	// Cas HEXA (#RRGGBBAA ou #RGBA)
	if (color.startsWith('#')) {
		// format #RRGGBBAA
		if (color.length === 9 && color.endsWith('00')) return true;

		// format #RGBA (ex: #FFF0)
		if (color.length === 5 && color.endsWith('0')) return true;
	}

	// Cas RGBA / RGB
	if (color.startsWith('rgba')) {
		const match = color.match(/rgba\((\s*\d+\s*,){3}\s*([\d.]+)\s*\)/);
		if (match) {
			const alpha = parseFloat(match[2]!);
			if (alpha === 0) return true;
		}
	}

	return false;
};

/**
 * Convertit une string "rgb()" ou "rgba()" en objet {r,g,b,a}
 */
function parseRGB(color: string) {
	const m = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
	if (!m) return { r: 0, g: 0, b: 0, a: 1 };
	return {
		r: parseInt(m[1]!),
		g: parseInt(m[2]!),
		b: parseInt(m[3]!),
		a: m[4] !== undefined ? parseFloat(m[4]) : 1,
	};
}

/**
 * Mélange le texte semi-transparent sur le fond
 */
function blend(fg: { r: number, g: number, b: number, a: number }, bg: { r: number, g: number, b: number, a: number }) {
	const a = fg.a + bg.a * (1 - fg.a);
	const r = (fg.r * fg.a + bg.r * bg.a * (1 - fg.a)) / a;
	const g = (fg.g * fg.a + bg.g * bg.a * (1 - fg.a)) / a;
	const b = (fg.b * fg.a + bg.b * bg.a * (1 - fg.a)) / a;
	return { r, g, b, a };
}

/**
 * Calcul de la luminance relative WCAG
 */
function luminance(c: { r: number, g: number, b: number }) {
	const [r, g, b] = [c.r, c.g, c.b].map(v => {
		const s = v / 255;
		return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
	});
	return 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
}

/**
 * Calcule le ratio de contraste entre deux couleurs RGBA
 */
function contrastRatio(fg: { r: number, g: number, b: number, a: number }, bg: {
	r: number,
	g: number,
	b: number,
	a: number
}) {
	const blended = fg.a < 1 ? blend(fg, bg) : fg;
	const L1 = luminance(blended);
	const L2 = luminance(bg);
	return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);
}

/**
 * Retourne true si le texte est lisible sur le fond
 */
export function isReadable(fgStr: string, bgStr: string, minRatio = 4.5) {
	const fg = parseRGB(fgStr);
	const bg = parseRGB(bgStr);
	return contrastRatio(fg, bg) >= minRatio;
}

export function isHexColor(color: string) {
	return /^#(([0-9A-Fa-f]{2}){3,4}|[0-9A-Fa-f]{3})$/.test(color);
}